import { useState } from 'react';
import { api, ApiError, formatDate, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { APPRENTICESHIP_STATES, type Apprenticeship, type ApprenticeshipLogEntry, type Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

/**
 * Trade apprenticeships + the mobile logbook
 * (packages/modules/m29-tertiary-tvet/src/routes/apprenticeships.ts).
 * Registration makes a best-effort live call to M28 Education Services'
 * edu:verify-qualification to snapshot the apprentice's secondary/NATIONAL/
 * TVET exam record (never blocks intake — see entryQualificationSource).
 * Logbook entries are the apprentice's own mobile self-service; this
 * console (SQA_ASSESSOR) can countersign/dispute on the supervisor's behalf
 * and runs the suspend/reinstate/terminate/complete lifecycle (completion
 * issues a QR-verifiable DocVault certificate).
 */
export function Apprenticeships() {
  const { persona } = useAuth();
  const isAssessor = persona?.roles.includes('SQA_ASSESSOR') ?? false;
  const [trade, setTrade] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const qs = new URLSearchParams({ limit: '50' });
  if (trade) qs.set('trade', trade);
  if (status) qs.set('status', status);
  if (q) qs.set('q', q);
  const listQ = useApiQuery<Page<Apprenticeship>>(`/api/v1/m29/apprenticeships?${qs.toString()}`);

  return (
    <div>
      <PageHeader
        title="Apprenticeships"
        subtitle="Trade apprenticeship register, entry-qualification snapshot, and the supervisor logbook."
        actions={
          isAssessor ? (
            <button className="btn-primary" onClick={() => setShowCreate(true)}>
              + Register apprenticeship
            </button>
          ) : undefined
        }
      />
      {!isAssessor ? (
        <div className="mb-4">
          <ErrorNote message="This list is scoped to your own employer identity unless you hold the SQA Assessor role." />
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap gap-2">
        <input className="field-input !w-52" placeholder="Search apprentice NIN / name…" value={q} onChange={(e) => setQ(e.target.value)} />
        <input className="field-input !w-40" placeholder="Trade…" value={trade} onChange={(e) => setTrade(e.target.value)} />
        <select className="field-input !w-40" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {APPRENTICESHIP_STATES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No apprenticeships match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Apprentice</th>
                <th className="table-head">Trade</th>
                <th className="table-head">Employer</th>
                <th className="table-head">Supervisor</th>
                <th className="table-head">Entry qual.</th>
                <th className="table-head">State</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((a) => (
                <tr key={a.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetailId(a.id)}>
                  <td className="table-cell">
                    <div className="font-medium">{a.apprenticeName}</div>
                    <div className="font-mono text-xs text-slate-400">{a.apprenticeNin}</div>
                  </td>
                  <td className="table-cell">{a.trade}</td>
                  <td className="table-cell text-xs text-slate-500">{a.employerName}</td>
                  <td className="table-cell text-xs text-slate-500">{a.supervisorName}</td>
                  <td className="table-cell">
                    <StatusBadge status={a.entryQualificationSource === 'M28' ? 'VALID' : 'NONE'} />
                  </td>
                  <td className="table-cell">
                    <StatusBadge status={a.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate ? (
        <CreateApprenticeshipModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
      {detailId ? (
        <ApprenticeshipDetailModal id={detailId} isAssessor={isAssessor} onClose={() => setDetailId(null)} onChanged={() => listQ.reload()} />
      ) : null}
    </div>
  );
}

function CreateApprenticeshipModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [employerName, setEmployerName] = useState('');
  const [apprenticeNin, setApprenticeNin] = useState('');
  const [apprenticeName, setApprenticeName] = useState('');
  const [trade, setTrade] = useState('');
  const [supervisorNin, setSupervisorNin] = useState('');
  const [supervisorName, setSupervisorName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [expectedEndDate, setExpectedEndDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m29/apprenticeships', {
        employerName,
        apprenticeNin,
        apprenticeName,
        trade,
        supervisorNin,
        supervisorName,
        startDate: new Date(startDate).toISOString(),
        expectedEndDate: new Date(expectedEndDate).toISOString(),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to register apprenticeship');
    } finally {
      setBusy(false);
    }
  };

  const valid =
    employerName.trim().length >= 2 &&
    apprenticeNin.trim().length >= 5 &&
    apprenticeName.trim().length >= 2 &&
    trade.trim().length >= 2 &&
    supervisorNin.trim().length >= 5 &&
    supervisorName.trim().length >= 2 &&
    startDate &&
    expectedEndDate;

  return (
    <Modal title="Register apprenticeship" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <p className="text-xs text-slate-400">
          Registers against your own signed-in identity as the employer of record (X-Acting-For business mandates are not wired up in
          this dev build — see build report).
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Employer name">
            <input className="field-input" value={employerName} onChange={(e) => setEmployerName(e.target.value)} />
          </Field>
          <Field label="Trade">
            <input className="field-input" value={trade} onChange={(e) => setTrade(e.target.value)} placeholder="e.g. Electrical Installation" />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Apprentice NIN">
            <input className="field-input" value={apprenticeNin} onChange={(e) => setApprenticeNin(e.target.value)} />
          </Field>
          <Field label="Apprentice name">
            <input className="field-input" value={apprenticeName} onChange={(e) => setApprenticeName(e.target.value)} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Supervisor NIN">
            <input className="field-input" value={supervisorNin} onChange={(e) => setSupervisorNin(e.target.value)} />
          </Field>
          <Field label="Supervisor name">
            <input className="field-input" value={supervisorName} onChange={(e) => setSupervisorName(e.target.value)} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Start date">
            <input className="field-input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </Field>
          <Field label="Expected end date">
            <input className="field-input" type="date" value={expectedEndDate} onChange={(e) => setExpectedEndDate(e.target.value)} />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !valid}>
            {busy ? 'Registering…' : 'Register apprenticeship'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ApprenticeshipDetailModal({
  id,
  isAssessor,
  onClose,
  onChanged,
}: {
  id: string;
  isAssessor: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const detailQ = useApiQuery<Apprenticeship>(`/api/v1/m29/apprenticeships/${id}`);
  const logbookQ = useApiQuery<{ items: ApprenticeshipLogEntry[] }>(`/api/v1/m29/apprenticeships/${id}/logbook`);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showReasonFor, setShowReasonFor] = useState<'suspend' | 'terminate' | null>(null);
  const [reason, setReason] = useState('');
  const [disputingId, setDisputingId] = useState<string | null>(null);
  const [disputeReason, setDisputeReason] = useState('');

  const apprenticeship = detailQ.data;

  const act = async (path: string, body?: unknown) => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m29/apprenticeships/${id}${path}`, body);
      detailQ.reload();
      onChanged();
      setShowReasonFor(null);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const actLog = async (entryId: string, path: string, body?: unknown) => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m29/apprenticeships/${id}/logbook/${entryId}${path}`, body);
      logbookQ.reload();
      setDisputingId(null);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const entries = logbookQ.data?.items ?? [];
  const countersigned = entries.filter((e) => e.countersignStatus === 'COUNTERSIGNED');
  const unresolved = entries.filter((e) => e.countersignStatus !== 'COUNTERSIGNED');

  return (
    <Modal title="Apprenticeship" onClose={onClose} wide>
      {detailQ.loading || !apprenticeship ? (
        <Spinner />
      ) : (
        <div className="space-y-4 text-sm">
          {error ? <ErrorNote message={error} /> : null}
          <div className="flex items-center gap-2">
            <StatusBadge status={apprenticeship.state} />
            <span className="font-semibold">{apprenticeship.apprenticeName}</span>
            <span className="font-mono text-xs text-slate-400">{apprenticeship.apprenticeNin}</span>
          </div>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5">
            <div className="flex justify-between">
              <dt className="text-slate-500">Trade</dt>
              <dd className="font-medium">{apprenticeship.trade}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Employer</dt>
              <dd className="font-medium">{apprenticeship.employerName}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Supervisor</dt>
              <dd className="font-medium">
                {apprenticeship.supervisorName} <span className="font-mono text-xs text-slate-400">{apprenticeship.supervisorNin}</span>
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Period</dt>
              <dd className="font-medium">
                {formatDate(apprenticeship.startDate)} → {formatDate(apprenticeship.expectedEndDate)}
              </dd>
            </div>
          </dl>

          <div className="border-t border-slate-100 pt-3">
            <div className="mb-1.5 flex items-center justify-between">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Entry qualification (M28 snapshot)</div>
              {isAssessor ? (
                <button className="btn-secondary !px-2.5 !py-0.5 text-[11px]" disabled={busy} onClick={() => act('/refresh-entry-qualification')}>
                  Re-pull from M28
                </button>
              ) : null}
            </div>
            {apprenticeship.entryQualificationSource === 'M28' ? (
              <pre className="overflow-x-auto rounded-lg bg-slate-50 p-2 text-[11px] text-slate-600">
                {JSON.stringify(apprenticeship.entryQualificationSnapshot, null, 2)}
              </pre>
            ) : (
              <p className="text-xs text-slate-400">No M28 exam record found (or M28 was unreachable) — never blocks intake.</p>
            )}
          </div>

          <div className="border-t border-slate-100 pt-3">
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Logbook — {countersigned.length} countersigned / {entries.length} total
            </div>
            {logbookQ.loading ? (
              <Spinner />
            ) : entries.length === 0 ? (
              <p className="text-xs text-slate-400">No logbook entries yet — the apprentice logs hours from the mobile app.</p>
            ) : (
              <ul className="max-h-64 space-y-1.5 overflow-y-auto">
                {entries.map((e) => (
                  <li key={e.id} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs">
                    <div className="flex items-center justify-between">
                      <span>
                        {formatDate(e.entryDate)} — {e.hoursLogged}h
                      </span>
                      <StatusBadge status={e.countersignStatus} />
                    </div>
                    <p className="mt-1 text-slate-500">{e.tasksPerformed}</p>
                    {e.disputeReason ? <p className="mt-1 text-rose-600">Disputed: {e.disputeReason}</p> : null}
                    {e.countersignStatus === 'PENDING' ? (
                      disputingId === e.id ? (
                        <div className="mt-2 flex items-center gap-2">
                          <input className="field-input flex-1 !py-1 text-xs" placeholder="Dispute reason…" value={disputeReason} onChange={(ev) => setDisputeReason(ev.target.value)} />
                          <button
                            className="btn-danger !px-2 !py-0.5 text-[11px]"
                            disabled={busy || disputeReason.trim().length < 3}
                            onClick={() => actLog(e.id, '/dispute', { disputeReason })}
                          >
                            Confirm dispute
                          </button>
                          <button className="text-slate-500" onClick={() => setDisputingId(null)}>
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="mt-2 flex gap-1.5">
                          <button className="btn-success !px-2 !py-0.5 text-[11px]" disabled={busy} onClick={() => actLog(e.id, '/countersign')}>
                            Countersign
                          </button>
                          <button className="btn-danger !px-2 !py-0.5 text-[11px]" onClick={() => setDisputingId(e.id)}>
                            Dispute
                          </button>
                        </div>
                      )
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {isAssessor ? (
            <div className="flex flex-wrap gap-1.5 border-t border-slate-100 pt-3">
              {apprenticeship.state === 'ACTIVE' ? (
                <>
                  <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => setShowReasonFor('suspend')}>
                    Suspend
                  </button>
                  <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => setShowReasonFor('terminate')}>
                    Terminate
                  </button>
                  <button
                    className="btn-success !px-2.5 !py-1 text-xs"
                    disabled={busy || countersigned.length === 0 || unresolved.length > 0}
                    title={countersigned.length === 0 ? 'No countersigned hours yet' : unresolved.length > 0 ? 'All entries must be countersigned first' : undefined}
                    onClick={() => act('/complete')}
                  >
                    Complete (issue certificate)
                  </button>
                </>
              ) : null}
              {apprenticeship.state === 'SUSPENDED' ? (
                <>
                  <button className="btn-success !px-2.5 !py-1 text-xs" disabled={busy} onClick={() => act('/reinstate')}>
                    Reinstate
                  </button>
                  <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => setShowReasonFor('terminate')}>
                    Terminate
                  </button>
                </>
              ) : null}
              {apprenticeship.state === 'COMPLETED' && apprenticeship.completionCertSerial ? (
                <p className="text-xs text-emerald-600">
                  Certificate <span className="font-mono">{apprenticeship.completionCertSerial}</span> issued {formatDateTime(apprenticeship.completionCertIssuedAt)}
                </p>
              ) : null}
            </div>
          ) : null}

          {showReasonFor ? (
            <div className="flex items-end gap-2 rounded-lg border border-slate-200 p-3">
              <Field label="Reason" className="!mb-0 flex-1">
                <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} />
              </Field>
              <button className="btn-danger" disabled={busy || reason.trim().length < 3} onClick={() => act(`/${showReasonFor}`, { reason })}>
                Confirm
              </button>
              <button className="btn-secondary" onClick={() => setShowReasonFor(null)}>
                Cancel
              </button>
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
